import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingUp, Users, Euro, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const SellROICalculator = () => {
  const [prospects, setProspects] = useState(20);
  const [avgDeal, setAvgDeal] = useState(500);

  // Without portfolio: 2% conversion, With: 8% conversion
  const withoutConversion = 0.02;
  const withConversion = 0.08;

  const clientsWithout = Math.round(prospects * withoutConversion * 12);
  const clientsWith = Math.round(prospects * withConversion * 12);
  const revenueWithout = clientsWithout * avgDeal;
  const revenueWith = clientsWith * avgDeal;
  const gain = revenueWith - revenueWithout;
  const roi = Math.round((gain / 149) * 100); // Based on "Clé en main" plan

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/5 to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent-foreground text-sm font-medium mb-4">
            <Calculator className="w-4 h-4" />
            Simulateur gratuit
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            Calculez votre{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              retour sur investissement
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Combien de clients et de revenus supplémentaires avec un portfolio professionnel ?
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Input panel */}
            <div className="p-8 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 space-y-8">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    Prospects par mois
                  </label>
                  <span className="text-2xl font-extrabold text-primary">{prospects}</span>
                </div>
                <Slider
                  value={[prospects]}
                  onValueChange={(v) => setProspects(v[0])}
                  min={5}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>5</span>
                  <span>100</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold flex items-center gap-2">
                    <Euro className="w-4 h-4 text-primary" />
                    Panier moyen (€)
                  </label>
                  <span className="text-2xl font-extrabold text-primary">{avgDeal}€</span>
                </div>
                <Slider
                  value={[avgDeal]}
                  onValueChange={(v) => setAvgDeal(v[0])}
                  min={100}
                  max={5000}
                  step={100}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>100€</span>
                  <span>5 000€</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-secondary/50 border border-border/30">
                <p className="text-xs text-muted-foreground mb-2">Hypothèses utilisées :</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Sans portfolio : ~2% de conversion (moyenne web)</li>
                  <li>• Avec portfolio pro : ~8% de conversion</li>
                  <li>• Période : 12 mois</li>
                </ul>
              </div>
            </div>

            {/* Results panel */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border border-primary/20 space-y-6">
              <div className="text-center mb-4">
                <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
                <h3 className="text-lg font-bold">Votre projection sur 12 mois</h3>
              </div>

              {/* Without */}
              <div className="p-4 rounded-xl bg-card/40 border border-border/30">
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">Sans portfolio pro</p>
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl font-bold text-muted-foreground">{clientsWithout} clients</span>
                  <span className="text-lg font-semibold text-muted-foreground">{revenueWithout.toLocaleString("fr-FR")}€</span>
                </div>
              </div>

              {/* With */}
              <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                <p className="text-xs text-primary mb-1 uppercase tracking-wider font-semibold">Avec Folio Studio</p>
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl font-bold text-foreground">{clientsWith} clients</span>
                  <span className="text-lg font-semibold text-primary">{revenueWith.toLocaleString("fr-FR")}€</span>
                </div>
              </div>

              {/* Gain */}
              <motion.div
                className="p-5 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground text-center"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <p className="text-sm font-medium opacity-90 mb-1">Gain potentiel annuel</p>
                <p className="text-4xl font-extrabold">+{gain.toLocaleString("fr-FR")}€</p>
                <p className="text-sm mt-1 opacity-80">
                  ROI de <strong>{roi}%</strong> sur la formule Clé en main
                </p>
              </motion.div>

              <a href="#pricing" className="block">
                <Button size="lg" className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group py-6">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Investir dans mon portfolio
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SellROICalculator;
